import _ from 'lodash';
import * as Ramda from 'ramda';
import { formatCurrency } from '@cowbell/ui/core';
import { moneyFormat2Decimals } from '../../../../../utils/appUtils';
import { toUniversalDate } from '../../../../../utils/date.utils';
import { retroActiveDateMap } from '../../../../agencies/quotes/PrimeX/utils/primeX.util';
import { ProductTypes } from '../../../../../types';
import type { RaterOptionKeysType } from '../types';
import type { CarrierEligibility } from '../../../../../api/quotes.api';
import type { Languages } from '../../../../../i18n/i18n.language-config';
import {
  NO_DECIMAL_FORMAT,
  deriveCurrencyFormat,
} from '../../../../../i18n/currencies';

/**
 * The Prime One Plus excess product. Excess quotes carry their terms in the
 * tower (global.value) rather than in the scalar global rater fields, so UW
 * surfaces branch on this. See FPX-5990.
 */
export const isExcessUwProductType = (productType?: string): boolean =>
  productType === ProductTypes.prime_one_plus_us;

/**
 * `currency` is the ISO 4217 code for the record these options belong to. When present, money
 * options format in that currency via the same `Intl` path `<CurrencyAmount>` uses, so a retention
 * dropdown cannot disagree with the premium rendered beside it (MS-417). When it is absent — a
 * payload shape that still carries no currency — the old language-derived format is kept rather
 * than guessing a symbol.
 */

const formatOptionAsLabelAndValue = (option: Option) => {
  return {
    label: `${option}`,
    value: option,
  };
};
const waitingPeriodOptionFormatter = (option: Option) => {
  return {
    label: `${option} Hours`,
    value: option,
  };
};

const moneyFormatter =
  (language: Languages, currency?: string | null) => (option: Option) => {
    if (currency == null) {
      return {
        label: deriveCurrencyFormat(language, option, NO_DECIMAL_FORMAT),
        value: option,
      };
    }

    return {
      // NO_DECIMAL_FORMAT's equivalent: these are whole-dollar rater options, and the legacy
      // format showed no cents. `formatCurrency` returns null rather than guessing a symbol when
      // the code is malformed, so fall back to the same '-' placeholder CurrencyAmount renders.
      label:
        formatCurrency(option as number, currency, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }) ?? '-',
      value: option,
    };
  };

export const retroactiveOptionsFormatter = (option: Option) => {
  let label;
  if (!retroActiveDateMap[option as keyof typeof retroActiveDateMap]) {
    label = toUniversalDate(option);
  } else {
    label = retroActiveDateMap[option as keyof typeof retroActiveDateMap];
  }

  return {
    label,
    value: option,
  };
};

/**
 * The fields whose options read as something other than money.
 *
 * A lookup rather than a chain of `if`s so that `hasNamedOptionFormat` below
 * cannot drift from it. The two were briefly separate, and the draft's staged
 * marker money-formatted a class code on the strength of the wrong one.
 */
const NAMED_OPTION_FORMATTERS: Readonly<
  Record<string, (option: Option) => { label: string; value: Option }>
> = {
  waitingPeriod: waitingPeriodOptionFormatter,
  retroactive: retroactiveOptionsFormatter,
  territorialLimit: formatOptionAsLabelAndValue,
  basisOfCover: formatOptionAsLabelAndValue,
  crimeClassCode: formatOptionAsLabelAndValue,
};

export function deriveOptionFormatter(
  language: Languages,
  name?: string,
  currency?: string | null
) {
  return (
    NAMED_OPTION_FORMATTERS[name ?? ''] ?? moneyFormatter(language, currency)
  );
}

/**
 * Whether this field has a format of its own, as opposed to falling through to
 * money.
 *
 * For callers that want to borrow the grid's label ONLY where the grid really
 * has one — money is this function's default, not a statement that the field is
 * money, and applying it to a string yields `$NaN`.
 */
export function hasNamedOptionFormat(name?: string): boolean {
  return Boolean(NAMED_OPTION_FORMATTERS[name ?? '']);
}

/**
 * The names this module specialises, for callers that need to compare the set
 * against one of their own rather than probe it a name at a time.
 *
 * These are camelCase, as the rater writes them. A caller holding lowercased
 * keys has to fold the case itself — comparing the two sets raw silently
 * matches nothing, which is how a test written to catch exactly that
 * mismatch passed while the mismatch was present.
 */
export function namedOptionFormatNames(): readonly string[] {
  return Object.keys(NAMED_OPTION_FORMATTERS);
}

type Option = number | string;

export function isValidDateString(dateString: string | number) {
  if (!_.isString(dateString)) {
    return false;
  }

  const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  return regex.test(dateString);
}

export function getIndicatedPricing(
  isLoading: boolean,
  isError: boolean,
  premium: number | undefined
) {
  if (isLoading) {
    return 'Calculating...';
  }

  if (isError) {
    return 'Error';
  }

  return moneyFormat2Decimals(premium);
}

/**
 * Derives the retro update path based on the provided value.
 * If the value is a valid date string, it returns an object with the `date` property.
 * If the value is a number or string, it returns an object with the `period` property.
 * @param value - The value to derive the retro update path from.
 * @returns An object with the derived retro update path.
 */
export function deriveRetroUpdatePath(value: number | string) {
  if (isValidDateString(value)) {
    return {
      props: {
        date: { value },
      },
    };
  }

  return {
    props: {
      period: { value },
    },
  };
}

export const mergeAndFormatDynamicFieldValues = (
  ...fieldValues: (Record<string, any> | undefined)[]
) => {
  if (fieldValues.some(Ramda.isNil)) {
    return undefined;
  }

  return fieldValues.reduce<FieldValue[]>((acc, current) => {
    if (!current) {
      return acc;
    }

    Object.keys(current).forEach((key) => {
      acc?.push({
        fieldId: key,
        value: current[key],
      });
    });

    return acc;
  }, []);
};

type FieldValue = { fieldId: string; value: any };

export function pullGlobalValues(data: any) {
  return _.pick(data, [
    'aggLimit',
    'deductible',
    'waitingPeriod',
    'retroactivePeriod',
    'retroactiveDate',
  ]);
}

export function deriveRaterOptionsFormatter(
  name: 'carrier'
): typeof carrierOptionsFormatter;
export function deriveRaterOptionsFormatter(
  name: 'productVersion'
): typeof raterVersionsFormatter;
export function deriveRaterOptionsFormatter(
  name: RaterOptionKeysType
): typeof carrierOptionsFormatter | typeof raterVersionsFormatter;
export function deriveRaterOptionsFormatter(name: RaterOptionKeysType) {
  if (name === 'carrier') {
    return carrierOptionsFormatter;
  }

  if (name === 'productVersion') {
    return raterVersionsFormatter;
  }

  throw new Error('Invalid rater option name');
}

const carrierOptionsFormatter = (option: CarrierEligibility) => {
  return {
    label: option.carrierName,
    value: option.treatyId,
    policyPrefix: option.policyPrefix,
    isAssigned: option.isAssigned,
    applicable: option.applicable,
    overridable: option.overridable,
    rules: option.rules,
  };
};

const raterVersionsFormatter = (option: number) => {
  return {
    label: `(Product version) - ${option}`,
    value: option.toString(),
  };
};
